import { ref, computed } from 'vue'
import { getAuth } from 'firebase/auth'
import {
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch
} from 'firebase/firestore'
import { FirebaseProvider } from '@/infrastructure/providers/FirebaseProvider'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'
import type { ModulePermissions } from '@/domain/types/Role'
import {
  ROLE_DESCRIPTION_MAX_LENGTH,
  buildRoleActor,
  normalizeCustomRole,
  normalizeRoleHistoryEntry,
  roleSnapshot,
  samePermissions,
  sanitizeRolePermissions,
  validateRoleName,
  type CustomRole,
  type RoleHistoryEntry
} from '@/domain/types/CustomRole'

export interface RoleInput { name: string; description: string; permissions: ModulePermissions }

export function useCompanyRoles() {
  const authStore = useAuthStore()
  const companyStore = useCompanyStore()
  const db = FirebaseProvider.getInstance().getFirestore()

  const roles = ref<CustomRole[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const activeRoles = computed(() => roles.value.filter((r) => r.active))

  const currentCompanyId = () => {
    const companyId = companyStore.selectedCompanyId
    if (!companyId) throw new Error('No hay compañía seleccionada')
    return companyId
  }

  /** Gestionar roles es solo del admin de la compañía activa (D7). */
  const assertCanManageRoles = () => {
    if (!authStore.isAdminOrSuperAdmin) {
      throw new Error('Solo un administrador puede gestionar roles')
    }
  }

  const rolesCol = (cid: string) => collection(db, 'Companies', cid, 'roles')
  const historyCol = (cid: string) => collection(db, 'Companies', cid, 'roleHistory')

  const sortRoles = () => {
    roles.value = [...roles.value].sort((a, b) => a.name.localeCompare(b.name, 'es'))
  }

  const fetchRoles = async () => {
    try {
      isLoading.value = true
      error.value = null
      const snapshot = await getDocs(rolesCol(currentCompanyId()))
      roles.value = snapshot.docs.map((d) => normalizeCustomRole(d.id, d.data()))
      sortRoles()
    } catch (err: any) {
      error.value = err.message || 'Error cargando los roles'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /** Valida y normaliza la entrada; lanza el mensaje de negocio si algo falla. */
  const prepareInput = (input: RoleInput, selfId?: string) => {
    const nameError = validateRoleName(input.name, roles.value, selfId)
    if (nameError) throw new Error(nameError)
    const permissions = sanitizeRolePermissions(input.permissions)
    if (!Object.keys(permissions).length) throw new Error('Selecciona al menos un permiso')
    return {
      name: input.name.trim(),
      description: (input.description ?? '').trim().slice(0, ROLE_DESCRIPTION_MAX_LENGTH),
      permissions
    }
  }

  const createRole = async (input: RoleInput): Promise<CustomRole> => {
    assertCanManageRoles()
    const cid = currentCompanyId()
    const { name, description, permissions } = prepareInput(input)
    const actor = buildRoleActor(authStore.user, getAuth().currentUser?.uid)

    const roleRef = doc(rolesCol(cid))
    const batch = writeBatch(db)
    batch.set(roleRef, {
      name,
      description,
      permissions,
      active: true,
      createdAt: serverTimestamp(),
      createdBy: actor,
      updatedAt: serverTimestamp(),
      updatedBy: actor
    })
    batch.set(doc(historyCol(cid)), {
      type: 'role_created',
      roleId: roleRef.id,
      roleName: name,
      targetUser: null,
      before: null,
      after: roleSnapshot({ name, description, permissions, active: true }),
      actor,
      at: serverTimestamp()
    })
    await batch.commit()

    const now = new Date()
    const created: CustomRole = {
      id: roleRef.id,
      name,
      description,
      permissions,
      active: true,
      createdAt: now,
      createdBy: actor,
      updatedAt: now,
      updatedBy: actor
    }
    roles.value = [...roles.value, created]
    sortRoles()
    return created
  }

  const updateRole = async (role: CustomRole, input: RoleInput): Promise<boolean> => {
    assertCanManageRoles()
    const cid = currentCompanyId()
    const { name, description, permissions } = prepareInput(input, role.id)

    if (
      name === role.name &&
      description === role.description &&
      samePermissions(role.permissions, permissions)
    ) {
      return false
    }

    const actor = buildRoleActor(authStore.user, getAuth().currentUser?.uid)
    const batch = writeBatch(db)
    batch.update(doc(rolesCol(cid), role.id), {
      name,
      description,
      permissions,
      updatedAt: serverTimestamp(),
      updatedBy: actor
    })
    batch.set(doc(historyCol(cid)), {
      type: 'role_updated',
      roleId: role.id,
      roleName: name,
      targetUser: null,
      before: roleSnapshot(role),
      after: roleSnapshot({ ...role, name, description, permissions }),
      actor,
      at: serverTimestamp()
    })
    await batch.commit()

    role.name = name
    role.description = description
    role.permissions = permissions
    role.updatedAt = new Date()
    role.updatedBy = actor
    sortRoles()
    return true
  }

  const setRoleActive = async (role: CustomRole, active: boolean, assignedUsers: number): Promise<void> => {
    assertCanManageRoles()
    if (role.active === active) return
    if (!active && assignedUsers > 0) {
      throw new Error(`Hay ${assignedUsers} usuario(s) con este rol. Asígnales otro rol antes de desactivarlo.`)
    }

    const cid = currentCompanyId()
    const actor = buildRoleActor(authStore.user, getAuth().currentUser?.uid)
    const batch = writeBatch(db)
    batch.update(doc(rolesCol(cid), role.id), {
      active,
      updatedAt: serverTimestamp(),
      updatedBy: actor
    })
    batch.set(doc(historyCol(cid)), {
      type: active ? 'role_reactivated' : 'role_deactivated',
      roleId: role.id,
      roleName: role.name,
      targetUser: null,
      before: roleSnapshot(role),
      after: roleSnapshot({ ...role, active }),
      actor,
      at: serverTimestamp()
    })
    await batch.commit()

    role.active = active
  }

  const fetchHistory = async (max = 100, targetUid?: string): Promise<RoleHistoryEntry[]> => {
    if (targetUid) {
      // Sin orderBy ni limit para no necesitar un índice compuesto; el historial de
      // una persona es pequeño y se ordena aquí (los `at` nulos, al final).
      const own = await getDocs(
        query(historyCol(currentCompanyId()), where('targetUser.uid', '==', targetUid))
      )
      return own.docs
        .map((d) => normalizeRoleHistoryEntry(d.id, d.data()))
        .sort((x, y) => (y.at?.getTime() ?? -Infinity) - (x.at?.getTime() ?? -Infinity))
    }
    const snapshot = await getDocs(
      query(historyCol(currentCompanyId()), orderBy('at', 'desc'), limit(max))
    )
    return snapshot.docs.map((d) => normalizeRoleHistoryEntry(d.id, d.data()))
  }

  return {
    roles,
    activeRoles,
    isLoading,
    error,
    fetchRoles,
    createRole,
    updateRole,
    setRoleActive,
    fetchHistory
  }
}
